<template>
  <div
    class="form-coordinates"
    :class="[{ preview: preview }, ...wrapperClasses]"
  >
    <div class="title flex mb-2">
      {{ title }}<span v-if="mandatory" class="required ml-2">*</span>
      <div
        v-if="!preview"
        @click="onClickHelp"
        class="flex cursor-pointer ml-2"
      >
        <feather-icon
          :icon="'HelpCircleIcon'"
          class="inline-flex rounded-full"
          svgClasses="h-5"
        ></feather-icon>
      </div>
    </div>
    <div v-if="showPlainText" class="flex">
      <div class="editable-input-area" @click="isEdit = true">
        <span class="editable-input-text">{{
          inputValue ? displayInputValue : "-"
        }}</span>
      </div>
      <common-updated-label
        :updatedAt="updateDateLabel"
        :createdAt="createDateLabel"
      />
      <div class="mandatory-switch flex" v-if="showMandatorySwitch">
        <vs-switch color="#2D9CDB" class="ml-6" v-model="mandatoryValue" />
        <span class="ml-2">必須</span>
      </div>
      <template v-if="showStatus">
        <status-chip
          v-if="isStatusFixed"
          class="ml-3 mr-2"
          :label="status.label"
          :color="status.color"
        ></status-chip>
        <status-dropdown-select
          v-else
          class="ml-3 mr-2"
          :options="filteredStatusOptions"
          :value.sync="status"
          :onClickOption="onStatusChange"
          clickEvent
        ></status-dropdown-select>
      </template>
    </div>
    <div v-else class="flex row-gap-8 col-gap-8 w-full">
      <vs-input
        class="w-full"
        placeholder="緯度(座標)"
        v-model="editInputLongitude"
        :danger="!isEditInputLongitudeValid"
        danger-text="数字のみ入力してください"
      />
      <vs-input
        class="w-full"
        placeholder="経度(座標)"
        v-model="editInputLatitude"
        :danger="!isEditInputLatitudeValid"
        danger-text="数字のみ入力してください"
      />
      <div class="btn-area flex gap-3" v-if="!preview">
        <common-button
          class="save-btn-template"
          title="保存"
          type="filled"
          :click="
            () => {
              onClickSaveButton();
            }
          "
          :btnStyle="{
            paddingLeft: 0,
            paddingRight: 0,
            width: '70px',
            height: '40px',
          }"
          :disabled="preview || !inputAllValid"
        ></common-button>
        <common-button
          class="cancel-btn-template"
          type="border"
          color="#BDBDBD"
          :click="
            () => {
              cancelEdit();
            }
          "
          :btnStyle="{
            paddingLeft: 0,
            paddingRight: 0,
            width: '70px',
            height: '40px',
            fontSize: '12px',
          }"
          text-color="#757575"
          title="キャンセル"
          :disabled="preview"
        ></common-button>
      </div>
    </div>
  </div>
</template>

<script>
import { ColorMixin } from "@/mixins";
import CommonButton from "../molecules/CommonButton.vue";
import { toYMDHm_jp } from "@/utils/entity/DateUtil";
import ValidationUtil from "@/utils/entity/ValidationUtil";
import CommonUpdatedLabel from "../molecules/CommonUpdatedLabel.vue";
import StatusDropdownSelect from "../organisms/StatusDropdownSelect.vue";
import StatusChip from "../molecules/StatusChip.vue";

export default {
  components: {
    CommonButton,
    CommonUpdatedLabel,
    StatusDropdownSelect,
    StatusChip,
  },
  mixins: [ColorMixin],
  name: "O.FormCoordinates",
  props: {
    wrapperClasses: {
      type: Array, // Array<String>
      default() {
        return [];
      },
    },
    preview: {
      type: Boolean,
      default: false,
    },
    onClickSave: {
      type: Function,
      default: () => {},
    },
    onSwitchChange: {
      type: Function,
      default: () => {},
    },
    onClickHelp: {
      type: Function,
      default: () => {},
    },
    onStatusChange: {
      type: Function,
      default: () => {},
    },
    title: {
      type: String,
    },
    inputValue: {
      type: Array,
    },
    mandatory: {
      type: Boolean,
    },
    isAdmin: {
      type: Boolean,
    },
    createDate: {
      type: Number,
    },
    updateDate: {
      type: Number,
    },
    initialSwitchValue: {
      type: Boolean,
      default: false,
    },
    statusOptions: {
      type: Array,
      default: () => [],
    },
    initialStatusValue: {
      type: Object,
    },
  },
  data() {
    return {
      isEdit: false,
      mandatoryValue: this.initialSwitchValue,
      editInputLongitude: null,
      editInputLatitude: null,
      status: null,
    };
  },
  watch: {
    mandatoryValue(value) {
      this.onSwitchChange(value);
    },
  },
  created() {
    this.mandatoryValue = this.initialSwitchValue;
    this.initializeEditInput();
  },
  computed: {
    isEditInputLongitudeValid() {
      if (this.preview) return true;

      return (
        this.editInputValue == "" ||
        ValidationUtil.isAllDigits(this.editInputLongitude)
      );
    },
    isEditInputLatitudeValid() {
      if (this.preview) return true;

      return (
        this.editInputValue == "" ||
        ValidationUtil.isAllDigits(this.editInputLatitude)
      );
    },
    inputAllValid() {
      return this.isEditInputLongitudeValid && this.isEditInputLatitudeValid;
    },
    displayInputValue() {
      if (this.inputValue) {
        return `緯度 ${this.inputValue[1]} 経度${this.inputValue[0]}`;
      } else {
        return "-";
      }
    },
    createDateLabel() {
      return toYMDHm_jp(this.createDate);
    },
    updateDateLabel() {
      return toYMDHm_jp(this.updateDate);
    },
    showPlainText() {
      if (!this.preview && !this.isEdit) {
        return true;
      } else {
        return false;
      }
    },
    showMandatorySwitch() {
      if (this.isAdmin && this.showPlainText) {
        return true;
      } else {
        return false;
      }
    },
    filteredStatusOptions() {
      return this.statusOptions.filter(element => element.value != 1);
    },
    isStatusFixed() {
      return this.status.value == 1 || !this.isAdmin;
    },
    showStatus() {
      return !this.preview && this.mandatory;
    },
  },

  methods: {
    initializeEditInput() {
      this.editInputLongitude = this.inputValue ? this.inputValue[1] : null;
      this.editInputLatitude = this.inputValue ? this.inputValue[0] : null;
      this.status = this.initialStatusValue;
    },
    cancelEdit() {
      this.isEdit = false;
      this.initializeEditInput();
    },
    onClickSaveButton() {
      this.isEdit = false;
      this.onClickSave([this.editInputLatitude, this.editInputLongitude]);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";
@import "@/assets/scss/aisea/_typography.scss";
@import "@/assets/scss/aisea/hull-insurance.scss";
.form-coordinates {
  min-height: 80px;
  width: 100%;
  &.preview {
    pointer-events: none;
  }
  .required {
    font-weight: bold;
    color: $aisea-red;
  }
  .update-date-label {
    font-size: 12px;
  }
  .editable-input-area {
    margin-right: 18px;
    transition: background-color 0.3s;
    flex: 1;
    cursor: pointer;
    .editable-input-text {
      line-height: 2.2;
    }
    &:hover {
      background: $hover-background-blue;
    }
  }
}
</style>
